import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  SHOP_DEPARTMENTS,
  FILTER_CONDITIONS,
  FILTER_COLOR_GROUPS,
  FILTER_MATERIALS,
  FILTER_LENGTH_GROUPS,
  BOOK_TYPES,
  BOOK_LANGUAGES,
  BOOK_AUDIENCES,
  PET_TYPES,
  FILTER_LABELS,
  EMPTY_BROWSE_FILTERS,
  getSubcategoriesForCategory,
  getBrowseSizeOptions,
  isClothingCategory,
  parseMultiFilterValue,
  toggleMultiFilterValue,
} from '../../constants/itemBrowseFilters';
import {
  KITCHEN_DINING_GROUPS,
  KITCHEN_DINING_ROOT,
  getKitchenDiningTypes,
  isHomeKitchenListingCategory,
  isKitchenDiningSubcategory,
  joinKitchenDiningSubcategoryPath,
  parseKitchenDiningSubcategoryPath,
} from '../../constants/kitchenDiningCategorySchema';
import BrandSelector from './BrandSelector';

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 1rem;
  align-items: center;
  margin-top: 0.1rem;
  margin-bottom: 0.4rem;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0.15rem 0;
`;

const ToolbarButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0;
  background: transparent;
  color: var(--primary-color);
  border: none;
  border-bottom: 1px solid var(--primary-color);
  border-radius: 0;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  min-height: 40px;
  &:hover { filter: brightness(0.95); }
`;

const ToolbarBadge = styled.span`
  background: transparent;
  color: var(--primary-color);
  padding: 0;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 700;
`;

const SortSelect = styled.select`
  padding: 0.35rem 0;
  border: none;
  border-bottom: 1px solid var(--border-color);
  border-radius: 0;
  background-color: transparent;
  min-height: 40px;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const ActiveFilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: white;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1.5px solid var(--border-color);
`;

const RemoveChipButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
`;

const ClearAllLink = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`;

const ModalOverlay = styled.div`
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  align-items: flex-end;
  justify-content: center;
  @media (min-width: 640px) {
    align-items: center;
  }
`;

const ModalContent = styled.div`
  background: white;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  border-radius: 1rem 1rem 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  @media (min-width: 640px) {
    border-radius: 1rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalBody = styled.div`
  padding: 1.25rem;
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid var(--border-color);
`;

const FooterButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-weight: 700;
  cursor: pointer;
  &.primary {
    background: var(--primary-color);
    color: white;
    border: none;
  }
  &.secondary {
    background: white;
    border: 1.5px solid var(--border-color);
  }
`;

const FilterGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const FilterSectionTitle = styled.h4`
  margin: 0 0 0.5rem;
  font-size: 1rem;
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 0.7rem;
  border: 1.5px solid var(--border-color);
  border-radius: 0.5rem;
`;

const FilterInput = styled.input`
  width: 100%;
  padding: 0.7rem;
  border: 1.5px solid var(--border-color);
  border-radius: 0.5rem;
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const ConditionChip = styled.button`
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  border: 1.5px solid ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--border-color)')};
  background: ${(p) => (p.$active ? 'var(--primary-color)' : 'white')};
  color: ${(p) => (p.$active ? 'white' : 'var(--text-primary)')};
  cursor: pointer;
  font-weight: 600;
`;

const formatActiveValue = (key, value) => {
  if (key === 'minPrice' || key === 'maxPrice') return `${value} EGP`;
  if (key === 'condition') return parseMultiFilterValue(value).join(', ');
  if (key === 'is_hijabi') return value === 'true' ? 'Hijabi' : 'Non-Hijabi';
  return value;
};

export { buildBrowseFetchParams } from '../../constants/itemBrowseFilters';

export default function BrowseFiltersToolbar({
  filters,
  onFiltersChange,
  categories = [],
  sort = '',
  onSortChange,
  showNearest = false,
  hideDepartmentFilter = false,
  hideConditionFilter = false,
  hideSecondaryFilters = false,
  showHijabiFilter = false,
  sizeOptions = [],
  colorOptions = [],
  silhouetteOptions = [],
  vibeOptions = [],
  trainOptions = [],
  weightOptions = [],
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filters);

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const activeChips = useMemo(() => {
    const out = [];
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (!value || key === 'view' || key === 'sort' || key === 'category') return;
      out.push({ key, label: FILTER_LABELS[key] || key, value: formatActiveValue(key, value) });
    });
    return out;
  }, [filters]);

  const apply = () => {
    onFiltersChange({ ...draft, sort });
    setOpen(false);
  };

  const removeFilter = (key) => {
    onFiltersChange({ ...filters, [key]: '' });
  };

  const clearAll = () => {
    onFiltersChange({ ...EMPTY_BROWSE_FILTERS, sort });
  };

  const kitchenFilter = parseKitchenDiningSubcategoryPath(
    isHomeKitchenListingCategory(draft.category) ? draft.subcategory : '',
  );

  const setKitchenFilter = (patch) => {
    const next = { ...kitchenFilter, ...patch };
    if (patch.group !== undefined) {
      next.type = '';
    }
    setDraft((d) => ({
      ...d,
      subcategory: joinKitchenDiningSubcategoryPath([
        KITCHEN_DINING_ROOT,
        next.group,
        next.type,
      ]),
    }));
  };

  return (
    <>
      <Toolbar>
        <ToolbarButton type="button" onClick={() => setOpen(true)}>
          ⚙️ Filters
          {activeChips.length > 0 && <ToolbarBadge>{activeChips.length}</ToolbarBadge>}
        </ToolbarButton>
        <SortSelect
          aria-label="Sort items"
          value={sort || ''}
          onChange={(e) => onSortChange(e.target.value)}
        >
          <option value="">Recommended</option>
          {showNearest && <option value="nearest">Nearest sellers first</option>}
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="price-low">Price: low → high</option>
          <option value="price-high">Price: high → low</option>
        </SortSelect>
      </Toolbar>

      {activeChips.length > 0 && (
        <ChipsRow>
          {activeChips.map((c) => (
            <ActiveFilterChip key={c.key}>
              <span>{c.label}:</span>
              <span>{c.value}</span>
              <RemoveChipButton type="button" onClick={() => removeFilter(c.key)} aria-label={`Remove ${c.label}`}>
                ×
              </RemoveChipButton>
            </ActiveFilterChip>
          ))}
          <ClearAllLink type="button" onClick={clearAll}>Clear all</ClearAllLink>
        </ChipsRow>
      )}

      <ModalOverlay $open={open} onClick={() => setOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <ModalHeader>
            <strong>Filters</strong>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close">×</button>
          </ModalHeader>
          <ModalBody>
            {!hideDepartmentFilter && (
              <FilterGroup>
                <FilterSectionTitle>Department</FilterSectionTitle>
                <FilterSelect
                  value={draft.department || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, department: e.target.value }))}
                >
                  <option value="">All departments</option>
                  {SHOP_DEPARTMENTS.map((dep) => (
                    <option key={dep} value={dep}>{dep}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            {categories.length > 0 && (
              <FilterGroup>
                <FilterSectionTitle>Category</FilterSectionTitle>
                <FilterSelect
                  value={draft.category || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setDraft((d) => ({
                      ...d,
                      category: val,
                      subcategory: '',
                      book_subcategory: '',
                      size: '',
                    }));
                  }}
                >
                  <option value="">All categories</option>
                  {categories.filter((c) => c.value).map((c) => (
                    <option key={c.value} value={c.value}>{c.icon} {c.name}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            {showHijabiFilter && (
              <FilterGroup>
                <FilterSectionTitle>Designed for</FilterSectionTitle>
                <ChipGroup>
                  {[
                    { label: 'Hijabi', value: 'true' },
                    { label: 'Non-Hijabi', value: 'false' },
                  ].map((option) => (
                    <ConditionChip
                      key={option.value}
                      type="button"
                      $active={draft.is_hijabi === option.value}
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          is_hijabi: d.is_hijabi === option.value ? '' : option.value,
                        }))
                      }
                    >
                      {option.label}
                    </ConditionChip>
                  ))}
                </ChipGroup>
              </FilterGroup>
            )}

            {getSubcategoriesForCategory(draft.category).length > 0 && (
              <FilterGroup>
                <FilterSectionTitle>Subcategory</FilterSectionTitle>
                <FilterSelect
                  value={
                    isKitchenDiningSubcategory(draft.subcategory || draft.book_subcategory)
                      ? KITCHEN_DINING_ROOT
                      : (draft.subcategory || draft.book_subcategory || '')
                  }
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      subcategory: e.target.value,
                      book_subcategory: d.category === 'Books' ? e.target.value : d.book_subcategory,
                    }))
                  }
                >
                  <option value="">All</option>
                  {getSubcategoriesForCategory(draft.category).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            {draft.category === 'Books' && (
              <>
                <FilterGroup>
                  <FilterSectionTitle>Book type</FilterSectionTitle>
                  <FilterSelect
                    value={draft.book_type || ''}
                    onChange={(e) => setDraft((d) => ({ ...d, book_type: e.target.value }))}
                  >
                    <option value="">Any</option>
                    {BOOK_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
                <FilterGroup>
                  <FilterSectionTitle>Language</FilterSectionTitle>
                  <FilterSelect
                    value={draft.book_language || ''}
                    onChange={(e) => setDraft((d) => ({ ...d, book_language: e.target.value }))}
                  >
                    <option value="">Any</option>
                    {BOOK_LANGUAGES.map((lang) => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
                <FilterGroup>
                  <FilterSectionTitle>Audience</FilterSectionTitle>
                  <FilterSelect
                    value={draft.book_audience || ''}
                    onChange={(e) => setDraft((d) => ({ ...d, book_audience: e.target.value }))}
                  >
                    <option value="">Any</option>
                    {BOOK_AUDIENCES.map((audience) => (
                      <option key={audience} value={audience}>{audience}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
              </>
            )}

            {draft.category === 'Pet Supplies' && (
              <FilterGroup>
                <FilterSectionTitle>Pet type</FilterSectionTitle>
                <FilterSelect
                  value={draft.pet_type || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, pet_type: e.target.value }))}
                >
                  <option value="">Any</option>
                  {PET_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            {isHomeKitchenListingCategory(draft.category) &&
              isKitchenDiningSubcategory(draft.subcategory) && (
              <>
                <FilterGroup>
                  <FilterSectionTitle>Kitchen category</FilterSectionTitle>
                  <FilterSelect
                    value={kitchenFilter.group || ''}
                    onChange={(e) => setKitchenFilter({ group: e.target.value })}
                  >
                    <option value="">All</option>
                    {KITCHEN_DINING_GROUPS.map((group) => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
                {kitchenFilter.group && (
                  <FilterGroup>
                    <FilterSectionTitle>Kitchen type</FilterSectionTitle>
                    <FilterSelect
                      value={kitchenFilter.type || ''}
                      onChange={(e) => setKitchenFilter({ type: e.target.value })}
                    >
                      <option value="">All</option>
                      {getKitchenDiningTypes(kitchenFilter.group).map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </FilterSelect>
                  </FilterGroup>
                )}
              </>
            )}

            {silhouetteOptions.length > 0 && (
              <FilterGroup>
                <FilterSectionTitle>Silhouette</FilterSectionTitle>
                <FilterSelect
                  value={draft.silhouette || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, silhouette: e.target.value }))}
                >
                  <option value="">Any</option>
                  {silhouetteOptions.map((silhouette) => (
                    <option key={silhouette} value={silhouette}>{silhouette}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            {vibeOptions.length > 0 && (
              <FilterGroup>
                <FilterSectionTitle>Overall style vibe</FilterSectionTitle>
                <FilterSelect
                  value={draft.vibe || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, vibe: e.target.value }))}
                >
                  <option value="">Any</option>
                  {vibeOptions.map((vibe) => (
                    <option key={vibe} value={vibe}>{vibe}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            {trainOptions.length > 0 && (
              <FilterGroup>
                <FilterSectionTitle>Train</FilterSectionTitle>
                <FilterSelect
                  value={draft.train || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, train: e.target.value }))}
                >
                  <option value="">Any</option>
                  {trainOptions.map((train) => (
                    <option key={train} value={train}>{train}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            {weightOptions.length > 0 && (
              <FilterGroup>
                <FilterSectionTitle>Weight</FilterSectionTitle>
                <FilterSelect
                  value={draft.weight_class || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, weight_class: e.target.value }))}
                >
                  <option value="">Any</option>
                  {weightOptions.map((weight) => (
                    <option key={weight} value={weight}>{weight}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            <FilterGroup>
              <FilterSectionTitle>Size</FilterSectionTitle>
              <FilterSelect value={draft.size || ''} onChange={(e) => setDraft((d) => ({ ...d, size: e.target.value }))}>
                <option value="">Any</option>
                {(sizeOptions.length > 0 ? sizeOptions : getBrowseSizeOptions(draft.category))
                  .map((s) => <option key={s} value={s}>{s}</option>)}
              </FilterSelect>
            </FilterGroup>

            {!hideSecondaryFilters && isClothingCategory(draft.category) && (
              <FilterGroup>
                <FilterSectionTitle>Length</FilterSectionTitle>
                <FilterSelect value={draft.length || ''} onChange={(e) => setDraft((d) => ({ ...d, length: e.target.value }))}>
                  <option value="">Any</option>
                  {Object.entries(FILTER_LENGTH_GROUPS).map(([group, options]) => (
                    <optgroup key={group} label={group}>
                      {options.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </optgroup>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            <FilterGroup>
              <FilterSectionTitle>Price range (EGP)</FilterSectionTitle>
              <FilterRow>
                <FilterInput
                  type="number"
                  min="0"
                  placeholder="Min"
                  value={draft.minPrice || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, minPrice: e.target.value }))}
                />
                <FilterInput
                  type="number"
                  min="0"
                  placeholder="Max"
                  value={draft.maxPrice || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, maxPrice: e.target.value }))}
                />
              </FilterRow>
            </FilterGroup>

            {!hideSecondaryFilters && (
              <FilterGroup>
                <FilterSectionTitle>Brand</FilterSectionTitle>
                <BrandSelector
                  mode="filter"
                  filterValue={draft.brand || ''}
                  onFilterChange={(brandName) => setDraft((d) => ({ ...d, brand: brandName }))}
                />
              </FilterGroup>
            )}

            {!hideConditionFilter ? (
            <FilterGroup>
              <FilterSectionTitle>Condition</FilterSectionTitle>
              <ChipGroup>
                {FILTER_CONDITIONS.map((c) => {
                  const selected = parseMultiFilterValue(draft.condition).includes(c);
                  return (
                    <ConditionChip
                      key={c}
                      type="button"
                      $active={selected}
                      onClick={() =>
                        setDraft((d) => ({ ...d, condition: toggleMultiFilterValue(d.condition, c) }))
                      }
                    >
                      {c}
                    </ConditionChip>
                  );
                })}
              </ChipGroup>
            </FilterGroup>
            ) : null}

            <FilterGroup>
              <FilterSectionTitle>Color</FilterSectionTitle>
              <FilterSelect value={draft.color || ''} onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}>
                <option value="">Any</option>
                {colorOptions.length > 0
                  ? colorOptions.map((c) => <option key={c} value={c}>{c}</option>)
                  : Object.entries(FILTER_COLOR_GROUPS).map(([group, options]) => (
                    <optgroup key={group} label={group}>
                      {options.map((c) => (
                        <option key={`${group}-${c}`} value={c}>{c}</option>
                      ))}
                    </optgroup>
                  ))}
              </FilterSelect>
            </FilterGroup>

            {!hideSecondaryFilters && (
              <FilterGroup>
                <FilterSectionTitle>Material</FilterSectionTitle>
                <FilterSelect value={draft.material || ''} onChange={(e) => setDraft((d) => ({ ...d, material: e.target.value }))}>
                  <option value="">Any</option>
                  {FILTER_MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
                </FilterSelect>
              </FilterGroup>
            )}
          </ModalBody>
          <ModalFooter>
            <FooterButton type="button" className="secondary" onClick={() => setDraft({ ...EMPTY_BROWSE_FILTERS })}>
              Clear all
            </FooterButton>
            <FooterButton type="button" className="primary" onClick={apply}>
              Apply
            </FooterButton>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </>
  );
}
